/**
 * How the creature is feeling.
 *
 * This is the rule book for the whole character: given the time of day and
 * what Sam has logged, it decides which face to wear and what to say. It is deliberately
 * one small, pure function with no React and no network in it, so it is easy
 * to read, easy to change, and easy to try out.
 *
 * ---------------------------------------------------------------------------
 * If you want to change the creature's personality, this is the file.
 *
 *   - to change what it SAYS, edit the `lines` for a mood below
 *   - to change WHEN it feels something, edit the order of the checks in
 *     `moodFor` -- the first one that matches wins, so the ones at the top
 *     are the ones that matter most
 *   - to change its daily routine, edit `rhythm`
 *   - to change what it says while eating, sipping or pooping, or when it is
 *     tapped, edit `momentLines`
 *   - to change which reactions a tap can get, edit `tapReactions`
 *   - to change which food it eats or what it sips, edit `foods` and `drinks`
 *   - to change how it LOOKS, see components/Creature.tsx instead
 *   - to change how often it poops, see lib/poops.ts
 *
 * One rule worth keeping: it is never mean. It notices, it encourages, and it
 * is pleased to see her. It never scolds her for what she ate, and it never
 * implies she has failed at anything.
 */
import type { Day } from "./api";
import { toKey } from "./day";

export type Mood =
  | "asleep"
  | "waiting"
  | "waking"
  | "perky"
  | "peckish"
  | "dreamy"
  | "playful"
  | "cozy"
  | "happy"
  | "proud"
  | "strong"
  | "thirsty"
  | "sleepy"
  | "full";

export type Feeling = {
  mood: Mood;
  /** One short line, in the creature's voice. */
  line: string;
};

/**
 * What it might say in each mood. One is picked per day rather than at random
 * on every render, so the creature doesn't appear to change its mind every
 * time the screen redraws.
 */
const lines: Record<Mood, string[]> = {
  asleep: ["Still dreaming about toast…", "Zzz… morning already?", "Five more minutes."],
  // Not part of its day: this is how it looks when it is greeting someone.
  waiting: ["Hello!", "I'm ready when you are.", "Oh good, it's you."],
  waking: ["Is it morning? It feels like morning.", "*enormous yawn*", "Eyes open. Mostly."],
  perky: ["Good morning! I have so many plans.", "What a day to be a sprout!", "I'm ready when you are."],
  peckish: ["Is it lunchtime? It feels like lunchtime.", "Ooh, is it snack o'clock?", "I was just thinking about sandwiches."],
  dreamy: ["Just watching the clouds go by.", "I wonder what clouds taste like.", "Do you ever think about soup?"],
  playful: ["I've got the evening wiggles!", "Dance with me. Nobody's looking.", "Best part of the day!"],
  cozy: ["Blanket weather.", "Getting nice and snug.", "This is my favourite quiet bit."],
  happy: ["This is a good day.", "Nice balance so far!", "We're doing great."],
  proud: ["You MOVED today. I saw it.", "Look at you go!", "That walk counted. I counted it."],
  strong: ["So much protein. I feel enormous.", "Strong day!", "Protein goal: demolished."],
  thirsty: ["Have you had water? Be honest.", "A glass of water would be lovely.", "Hydrate, please!"],
  sleepy: ["Long day. Rest well.", "Winding down…", "Good night from me."],
  full: ["Comfortably stuffed.", "That was a lot of food. No notes.", "I need a lie down. Happily."],
};

/**
 * Picks one of the lines, but the same one all day.
 *
 * The date and mood together decide which, so the line only changes when the
 * mood does -- which makes the creature feel like it has a thought, rather
 * than like it is shuffling cards.
 */
function lineFor(mood: Mood, date: string): string {
  return pick(lines[mood], date + mood);
}

function pick(options: string[], key: string): string {
  let hash = 0;
  for (const character of key) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return options[hash % options.length];
}

/** Something happening right now, which takes over for a couple of seconds. */
export type MomentKind =
  | "eating"
  | "sipping"
  | "pooping"
  | "thanks"
  // The rest are what it does when it is tapped.
  | "giggle"
  | "jump"
  | "spin"
  | "hearts"
  | "wink"
  | "boop"
  | "dizzy"
  | "startled";

export type Moment = {
  kind: MomentKind;
  line: string;
  /** How long it lasts. Matches the animations in styles.css. */
  ms: number;
  /** The little picture of what it is eating or drinking. */
  food?: string;
};

const momentLines: Record<MomentKind, string[]> = {
  eating: ["Nom nom nom!", "Ooh, delicious.", "Mmm! Thank you!", "Yes. This. More of this."],
  sipping: ["Slurrrp.", "Ahh, refreshing.", "Glug glug glug.", "Mmm. Just what I needed."],
  pooping: ["Oop. Excuse me.", "Nothing to see here.", "That one's for you."],
  thanks: ["Ahh, much better!", "My hero.", "So fresh. So clean.", "Thanks! You're the best."],
  giggle: ["Hee hee! That tickles!", "Stoppp, hee hee!", "Not the tummy!"],
  jump: ["Whoa!", "Boing!", "How high was that?!"],
  spin: ["Wheeee!", "Did you see that spin?", "Ta-da!"],
  hearts: ["I like you too.", "You're my favourite.", "Aww, you."],
  wink: ["Our little secret.", "Looking good, by the way.", "You've still got it."],
  boop: ["Boop!", "You booped me!", "Boop right back at you."],
  dizzy: ["Everything is spinning…", "Okay, okay, I'm dizzy!", "How many of you are there?"],
  startled: ["Hm? Oh, it's you.", "Wha? I was awake. Totally.", "Mmph… five more minutes."],
};

const momentLengths: Record<MomentKind, number> = {
  eating: 2800,
  sipping: 2600,
  pooping: 1400,
  thanks: 1600,
  giggle: 1400,
  jump: 1100,
  spin: 1200,
  hearts: 1900,
  wink: 1500,
  boop: 1000,
  dizzy: 2600,
  startled: 1700,
};

/** The reactions a tap can get. Add one here and it joins the rotation. */
const tapReactions: MomentKind[] = ["giggle", "jump", "spin", "hearts", "wink", "boop"];

/** This many taps in a few seconds and it gets dizzy. */
export const tapsUntilDizzy = 6;

/**
 * What it does when tapped.
 *
 * Never the same thing twice in a row, so every tap visibly does something.
 * If it is sleeping, it is woken up first.
 */
export function reactToTap(mood: Mood, tapsInARow: number, previous: MomentKind | undefined): Moment {
  let kind: MomentKind;
  if (tapsInARow >= tapsUntilDizzy) kind = "dizzy";
  else if ((mood === "asleep" || mood === "sleepy") && tapsInARow === 1) kind = "startled";
  else kind = randomFrom(tapReactions.filter((reaction) => reaction !== previous));
  return { kind, line: randomFrom(momentLines[kind]), ms: momentLengths[kind] };
}

/** What it says while there is a poop lying around. */
const poopAroundLines = ["Um. I left you something.", "It wasn't me. (It was me.)", "Psst. Tap it?"];

export function poopAroundLine(date: string): string {
  return pick(poopAroundLines, date);
}

/**
 * The little food that flies into its mouth. The first word found in the name
 * of what she logged wins, so more specific words go first ("pineapple" before
 * "apple"). Anything it doesn't recognise gets a surprise from `snacks`.
 */
const foods: [string, string][] = [
  ["pizza", "🍕"], ["burger", "🍔"], ["fries", "🍟"], ["taco", "🌮"], ["burrito", "🌯"],
  ["sandwich", "🥪"], ["toast", "🍞"], ["bread", "🍞"], ["bagel", "🥯"], ["croissant", "🥐"],
  ["egg", "🍳"], ["banana", "🍌"], ["pineapple", "🍍"], ["apple", "🍎"], ["berr", "🍓"],
  ["orange", "🍊"], ["grape", "🍇"], ["avocado", "🥑"], ["carrot", "🥕"], ["broccoli", "🥦"],
  ["salad", "🥗"], ["sushi", "🍣"], ["rice", "🍚"], ["pasta", "🍝"], ["spaghetti", "🍝"],
  ["noodle", "🍜"], ["ramen", "🍜"], ["soup", "🍲"], ["chicken", "🍗"], ["steak", "🥩"],
  ["beef", "🥩"], ["bacon", "🥓"], ["fish", "🐟"], ["salmon", "🐟"], ["tuna", "🐟"],
  ["cheese", "🧀"], ["cookie", "🍪"], ["cake", "🍰"], ["chocolate", "🍫"], ["donut", "🍩"],
  ["ice cream", "🍦"], ["yogurt", "🥣"], ["yoghurt", "🥣"], ["oat", "🥣"], ["cereal", "🥣"], ["nut", "🥜"],
];
const snacks = ["🍎", "🥕", "🍪", "🍙", "🍓"];

/** The same idea for drinks. */
const drinks: [string, string][] = [
  ["water", "💧"], ["coffee", "☕"], ["latte", "☕"], ["espresso", "☕"], ["cappuccino", "☕"],
  ["boba", "🧋"], ["bubble tea", "🧋"], ["tea", "🍵"], ["matcha", "🍵"], ["milk", "🥛"],
  ["juice", "🧃"], ["beer", "🍺"], ["wine", "🍷"], ["cocktail", "🍹"], ["coconut", "🥥"],
];
const sips = ["🥤", "🧃"];

function pictureFor(name: string, table: [string, string][], surprises: string[]): string {
  const lower = name.toLowerCase();
  return table.find(([word]) => lower.includes(word))?.[1] ?? randomFrom(surprises);
}

function randomFrom<T>(options: T[]): T {
  return options[Math.floor(Math.random() * options.length)];
}

export function momentOf(kind: "pooping" | "thanks"): Moment {
  return { kind, line: randomFrom(momentLines[kind]), ms: momentLengths[kind] };
}

export function eat(foodName: string): Moment {
  return {
    kind: "eating",
    line: randomFrom(momentLines.eating),
    ms: momentLengths.eating,
    food: pictureFor(foodName, foods, snacks),
  };
}

export function sip(drinkName: string): Moment {
  return {
    kind: "sipping",
    line: randomFrom(momentLines.sipping),
    ms: momentLengths.sipping,
    food: pictureFor(drinkName, drinks, sips),
  };
}

/**
 * Its daily routine: the hour each mood starts, earliest first. This is what
 * it does on an ordinary day when nothing else has its attention.
 */
const rhythm: [startHour: number, mood: Mood][] = [
  [0, "asleep"],
  [7, "waking"],
  [9, "perky"],
  [12, "peckish"],
  [14, "dreamy"],
  [17, "playful"],
  [20, "cozy"],
  [22, "sleepy"],
];

/** The hours it gently wonders about water, if she hasn't had a drink yet. */
const thirstyHours = [13, 16, 19];

/** How long it stays excited about something she has just logged. */
const excitedFor = 2 * 60 * 60 * 1000; // two hours

/**
 * Works out how the creature feels about a day.
 *
 * `now` is passed in rather than read from the clock so this can be tried out
 * at any hour.
 */
export function moodFor(day: Day, now: Date): Feeling {
  const { totals, settings, entries, burned, movedMinutes } = day;
  const hour = now.getHours();

  const overGoal = totals.calories > settings.goal_calories * 1.15;
  const proteinMet = totals.protein_g >= settings.goal_protein_g;
  const moved = burned > 0 || movedMinutes > 0;

  const justLogged = (kinds: string[]) =>
    entries.some(
      (entry) =>
        kinds.includes(entry.kind) && now.getTime() - new Date(entry.logged_at).getTime() < excitedFor,
    );

  // The order below IS the personality. The first match wins.
  const mood: Mood = (() => {
    // Looking back at an earlier day, the clock means nothing, so it just
    // remembers how the day went.
    if (day.date !== toKey(now)) {
      if (moved) return "proud";
      if (overGoal) return "full";
      if (proteinMet) return "strong";
      return "happy";
    }

    // Good news gets its attention for a while, then the day carries on.
    // Movement is the thing it is most pleased about, so it comes first.
    if (justLogged(["exercise"])) return "proud";

    const justAte = justLogged(["food", "drink"]);

    // Comfortably past the calorie goal, in a pleased way rather than a
    // disapproving one.
    if (justAte && overGoal) return "full";

    // Protein met is worth celebrating on its own.
    if (justAte && proteinMet) return "strong";

    // A gentle nudge, for an hour at a time so it never turns into nagging.
    if (thirstyHours.includes(hour) && !entries.some((entry) => entry.kind === "drink")) return "thirsty";

    // Otherwise, whatever this time of day usually brings.
    const usual = [...rhythm].reverse().find(([startHour]) => hour >= startHour)![1];

    // It doesn't ask about lunch straight after lunch, or snooze through a
    // very early breakfast.
    if (justAte && usual === "peckish") return "happy";
    if (justAte && usual === "asleep") return "waking";

    return usual;
  })();

  return { mood, line: lineFor(mood, day.date) };
}

/**
 * How far round the ring to draw, 0 to 1, with anything past the goal clamped
 * so the ring fills rather than wrapping round again.
 */
export function progress(value: number, goal: number): number {
  if (!Number.isFinite(goal) || goal <= 0) return 0;
  return Math.max(0, Math.min(1, value / goal));
}
